import React, { useEffect, useState, useRef } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Package, ExternalLink, Plus, Loader2, Trash2, TrendingUp, PoundSterling, Link2, Sparkles, RotateCw, ImagePlus, Star, GripVertical, FolderTree } from "lucide-react";

// Curated dropshipping admin panel — lets the AfriMarket team add hand-picked
// African-themed products (sourced from Alibaba/AliExpress) under the
// "AfriMarket Official" seller account. Private supplier info (URL, cost,
// notes) stays admin-only thanks to the backend's separate collection.

const empty = {
  title: "", description: "", category: "Fashion", price: "", currency: "GBP",
  country: "United Kingdom", images: "",
  supplier_url: "", supplier_cost: "", supplier_notes: "",
  estimated_shipping_min_days: 14, estimated_shipping_max_days: 28,
};

const CuratedListings = () => {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [showForm, setShowForm] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/curated-listings");
      setList(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load curated listings");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        price: parseFloat(form.price),
        supplier_cost: form.supplier_cost ? parseFloat(form.supplier_cost) : null,
        images: form.images.split(/[\n,]/).map((s) => s.trim()).filter(Boolean),
      };
      await api.post("/admin/curated-listings", payload);
      toast.success("Curated listing created 🎉");
      setForm(empty);
      setShowForm(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create listing");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this curated listing? Buyers won't see it anymore.")) return;
    try {
      await api.delete(`/admin/curated-listings/${id}`);
      toast.success("Removed");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  const toggleFeature = async (l) => {
    const next = !l.featured_landing;
    try {
      await api.post(`/admin/curated-listings/${l.listing_id}/feature`, { featured: next });
      setList((prev) => prev.map((x) => (x.listing_id === l.listing_id ? { ...x, featured_landing: next } : x)));
      toast.success(next ? "Added to landing showcase ✦" : "Removed from landing showcase");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update");
    }
  };

  const [autoFeaturing, setAutoFeaturing] = useState(false);
  const autoFeatureAfrican = async () => {
    setAutoFeaturing(true);
    try {
      const { data } = await api.post("/admin/curated-listings/auto-feature-african");
      toast.success(`${data.featured} African pieces showcased on landing · ${data.not_featured} kept in categories only`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to auto-feature");
    } finally {
      setAutoFeaturing(false);
    }
  };

  // ── Landing-showcase drag-to-reorder ─────────────────────────────────────
  const [featuredItems, setFeaturedItems] = useState([]);
  useEffect(() => {
    setFeaturedItems(
      list
        .filter((l) => l.featured_landing)
        .sort((a, b) => (a.featured_order ?? 9999) - (b.featured_order ?? 9999)),
    );
  }, [list]);
  const dragItem = useRef(null);
  const persistOrder = async (items) => {
    try {
      await api.post("/admin/curated-listings/reorder-featured", { ordered_ids: items.map((x) => x.listing_id) });
      toast.success("Showcase order saved");
    } catch (e) {
      toast.error("Couldn't save order");
    }
  };
  const onDrop = (toIdx) => {
    const from = dragItem.current;
    dragItem.current = null;
    if (from === null || from === toIdx) return;
    setFeaturedItems((prev) => {
      const next = [...prev];
      const [moved] = next.splice(from, 1);
      next.splice(toIdx, 0, moved);
      persistOrder(next);
      return next;
    });
  };

  const [seeding, setSeeding] = useState(false);
  const seedStarterPack = async () => {
    if (!window.confirm("Seed the 13-product starter catalogue? Existing listings with the same titles will be skipped.")) return;
    setSeeding(true);
    try {
      const { data } = await api.post("/admin/curated-listings/seed-starter-pack");
      toast.success(`Starter pack seeded: ${data.created} new, ${data.skipped} skipped (already present)`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to seed starter pack");
    } finally {
      setSeeding(false);
    }
  };

  const [seedingTrending, setSeedingTrending] = useState(false);
  const seedTrending2026 = async () => {
    if (!window.confirm("Seed the 12-product Trending 2026 catalogue (electronics + African fashion + beauty)? Existing listings with the same titles will be skipped.")) return;
    setSeedingTrending(true);
    try {
      const { data } = await api.post("/admin/curated-listings/seed-trending-2026");
      toast.success(`Trending 2026 seeded: ${data.created} new, ${data.skipped} skipped`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to seed trending pack");
    } finally {
      setSeedingTrending(false);
    }
  };

  // ── Destructive: wipe + re-seed everything ────────────────────────────────
  const [forceReseeding, setForceReseeding] = useState(false);
  const forceReseed = async () => {
    const typed = window.prompt(
      "⚠️ This DELETES every curated listing without paid orders, then re-seeds both packs from scratch.\n\nType REPLACE (in CAPS) to continue.",
      "",
    );
    if (typed !== "REPLACE") {
      if (typed !== null) toast.error("Cancelled — you didn't type REPLACE.");
      return;
    }
    setForceReseeding(true);
    try {
      const { data } = await api.post("/admin/curated-listings/force-reseed", { confirm: "REPLACE" });
      const starter = data.starter_pack || {};
      const trending = data.trending_2026 || {};
      toast.success(
        `Wiped ${data.wiped} · Starter ${starter.created || 0} new · Trending ${trending.created || 0} new` +
        (data.preserved_with_orders ? ` · ${data.preserved_with_orders} preserved (have orders)` : ""),
      );
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to force re-seed");
    } finally {
      setForceReseeding(false);
    }
  };

  // ── Top-up images so every curated listing has 4+ photos ─────────────────
  const [toppingUp, setToppingUp] = useState(false);
  const topupImages = async () => {
    setToppingUp(true);
    toast.message("Generating photos — this can take 1-2 minutes…");
    try {
      const { data } = await api.post(
        "/admin/curated-listings/topup-images",
        { target: 4 },
        { timeout: 240000 },  // 4-min client timeout — image gen is slow
      );
      toast.success(
        `Topped up ${data.topped_up} listings · generated ${data.generated} new images · ${data.already_ok} already had 4+`,
      );
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to top-up images");
    } finally {
      setToppingUp(false);
    }
  };


  // ── URL auto-import — paste any AliExpress/CJ/Amazon link → fill the form ──
  const [importUrl, setImportUrl] = useState("");
  const [importing, setImporting] = useState(false);
  const runImport = async () => {
    const url = importUrl.trim();
    if (!url) return;
    setImporting(true);
    try {
      const { data } = await api.post("/admin/curated-listings/import-url", { url });
      const imgs = (data.images || []).join("\n");
      // Map common currencies to symbols-aware "GBP" so the form's currency stays GBP by default.
      setForm((f) => ({
        ...f,
        title: data.title || f.title,
        description: data.description || f.description,
        images: imgs || f.images,
        price: data.price ? String(data.price) : f.price,
        supplier_url: url,
      }));
      setShowForm(true);
      toast.success(`Imported "${(data.title || "product").slice(0, 50)}"`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not import from that URL");
    } finally {
      setImporting(false);
    }
  };

  // ── Direct photo upload — pick images from phone/computer → hosted URLs ──
  const fileInputRef = useRef(null);
  const [uploadingImgs, setUploadingImgs] = useState(false);
  const uploadImages = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploadingImgs(true);
    try {
      const fd = new FormData();
      files.forEach((f) => fd.append("files", f));
      const { data } = await api.post("/admin/curated-listings/upload-images", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const newUrls = (data.urls || []).join("\n");
      setForm((f) => ({ ...f, images: f.images ? `${f.images}\n${newUrls}` : newUrls }));
      toast.success(`Uploaded ${data.urls?.length || 0} photo(s)`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Photo upload failed");
    } finally {
      setUploadingImgs(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  // ── Pull full CJ photo galleries onto already-imported listings ──────────
  const [pullingImages, setPullingImages] = useState(false);
  const [imgProgress, setImgProgress] = useState(null);
  const pullCjImages = async () => {
    setPullingImages(true);
    setImgProgress(null);
    try {
      const { data } = await api.post("/admin/curated-listings/refresh-cj-images");
      const jobId = data.job_id;
      toast(`Pulling CJ photos for ${data.total} products…`);
      const poll = async () => {
        try {
          const jr = await api.get(`/admin/cj/import-job/${jobId}`);
          const j = jr.data;
          setImgProgress({ processed: j.processed, total: j.total });
          if (j.status === "done") {
            toast.success(`Updated ${j.imported_count} products with full photo galleries${j.failed_count ? `, ${j.failed_count} skipped` : ""}`);
            setPullingImages(false);
            setImgProgress(null);
            load();
            return;
          }
        } catch (e) { /* keep polling */ }
        setTimeout(poll, 2000);
      };
      setTimeout(poll, 1500);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to pull CJ photos");
      setPullingImages(false);
      setImgProgress(null);
    }
  };

  const totalRevenue = list.reduce((sum, x) => sum + (x.revenue || 0), 0);

  // ── Backfill variants (sizes/lengths) + real descriptions from CJ ────────
  const [backfilling, setBackfilling] = useState(false);
  const [detailProgress, setDetailProgress] = useState(null);
  const backfillDetails = async () => {
    if (!window.confirm("Pull variants (sizes/lengths + prices) and full descriptions from CJ for every imported product? This updates live listings.")) return;
    setBackfilling(true);
    setDetailProgress(null);
    try {
      const { data } = await api.post("/admin/curated-listings/backfill-details");
      const jobId = data.job_id;
      toast(`Fetching variants & descriptions for ${data.total} products…`);
      const poll = async () => {
        try {
          const jr = await api.get(`/admin/cj/import-job/${jobId}`);
          const j = jr.data;
          setDetailProgress({ processed: j.processed, total: j.total });
          if (j.status === "done") {
            toast.success(`Updated ${j.imported_count} products with variants & descriptions${j.failed_count ? `, ${j.failed_count} skipped` : ""}`);
            setBackfilling(false);
            setDetailProgress(null);
            load();
            return;
          }
        } catch (e) { /* keep polling */ }
        setTimeout(poll, 2000);
      };
      setTimeout(poll, 1500);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to backfill details");
      setBackfilling(false);
      setDetailProgress(null);
    }
  };

  const [repricing, setRepricing] = useState(false);
  const repriceAll = async () => {
    if (!window.confirm("Re-price ALL dropshipping products to 1.5× their supplier cost? This updates live prices immediately.")) return;
    setRepricing(true);
    try {
      const { data } = await api.post("/admin/curated-listings/reprice");
      toast.success(`Re-priced ${data.repriced} products to 1.5× · ${data.skipped} unchanged`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to re-price");
    } finally {
      setRepricing(false);
    }
  };

  const [repairing, setRepairing] = useState(false);
  const repairImages = async () => {
    setRepairing(true);
    try {
      const { data } = await api.post("/admin/curated-listings/repair-images");
      toast.success(`Fixed photo galleries on ${data.fixed} products`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to fix photos");
    } finally {
      setRepairing(false);
    }
  };

  const [recategorizing, setRecategorizing] = useState(false);
  const recategorizeAll = async () => {
    if (!window.confirm("Re-sort ALL products into the correct categories (Men's Clothing, Beauty & Hair, Health & Wellness, etc.) based on their titles? Runs on this site's live data.")) return;
    setRecategorizing(true);
    try {
      const { data } = await api.post("/admin/listings/recategorize-all");
      toast.success(`Re-sorted ${data.recategorized} of ${data.scanned} products into the right categories`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to re-categorize");
    } finally {
      setRecategorizing(false);
    }
  };
  const totalSales = list.reduce((sum, x) => sum + (x.sales_count || 0), 0);
  const totalEstMargin = list.reduce((sum, x) => {
    const m = x.estimated_margin;
    return sum + (m ? m * (x.sales_count || 0) : 0);
  }, 0);

  return (
    <Card className="p-6 bg-white border-black/10" data-testid="curated-admin-panel">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-[#C05A3C] text-white flex items-center justify-center">
            <Package className="w-5 h-5" />
          </div>
          <div>
            <div className="label-overline text-[#C05A3C]">Dropshipping</div>
            <h3 className="font-display text-2xl leading-tight">Curated by AfriMarket</h3>
            <div className="text-xs text-muted-foreground">
              Hand-picked African-themed products under the AfriMarket Official account
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button
            variant="outline"
            onClick={seedStarterPack}
            disabled={seeding}
            data-testid="curated-seed-btn"
            className="border-[#4A5D23] text-[#4A5D23] hover:bg-[#4A5D23]/10"
          >
            {seeding ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Package className="w-4 h-4 mr-1" />}
            Seed starter pack
          </Button>
          <Button
            variant="outline"
            onClick={seedTrending2026}
            disabled={seedingTrending}
            data-testid="curated-seed-trending-btn"
            className="border-[#C05A3C] text-[#C05A3C] hover:bg-[#C05A3C]/10"
          >
            {seedingTrending ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <TrendingUp className="w-4 h-4 mr-1" />}
            Seed Trending 2026
          </Button>
          <Button
            variant="outline"
            onClick={forceReseed}
            disabled={forceReseeding}
            data-testid="curated-force-reseed-btn"
            className="border-red-300 text-red-700 hover:bg-red-50"
            title="Wipe all unpurchased curated listings then re-seed both packs"
          >
            {forceReseeding ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <RotateCw className="w-4 h-4 mr-1" />}
            Force re-seed
          </Button>
          <Button
            variant="outline"
            onClick={topupImages}
            disabled={toppingUp}
            data-testid="curated-topup-images-btn"
            className="border-[#4A5D23] text-[#4A5D23] hover:bg-[#4A5D23]/10"
            title="Ensure every curated listing has at least 4 photos — generates extra AI angles for any item below the target"
          >
            {toppingUp ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <ImagePlus className="w-4 h-4 mr-1" />}
            Top-up to 4 photos
          </Button>
          <Button
            variant="outline"
            onClick={autoFeatureAfrican}
            disabled={autoFeaturing}
            data-testid="curated-auto-feature-btn"
            className="border-[#D9923B] text-[#D9923B] hover:bg-[#D9923B]/10"
            title="Auto-sort: African pieces → landing showcase, the rest → category only"
          >
            {autoFeaturing ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Star className="w-4 h-4 mr-1" />}
            Auto-feature African
          </Button>
          <Button
            variant="outline"
            onClick={repairImages}
            disabled={repairing}
            data-testid="curated-repair-images-btn"
            className="border-[#C05A3C] text-[#C05A3C] hover:bg-[#C05A3C]/10"
            title="Instantly fix products that show only one photo (flattens stored galleries)"
          >
            {repairing ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <ImagePlus className="w-4 h-4 mr-1" />}
            Fix product photos
          </Button>
          <Button
            variant="outline"
            onClick={pullCjImages}
            disabled={pullingImages}
            data-testid="curated-pull-cj-images-btn"
            className="border-[#2A3D36] text-[#2A3D36] hover:bg-[#2A3D36]/10"
            title="Re-fetch the full CJ photo gallery for every CJ-imported product"
          >
            {pullingImages ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <ImagePlus className="w-4 h-4 mr-1" />}
            {pullingImages && imgProgress ? `Pulling ${imgProgress.processed}/${imgProgress.total}…` : "Pull CJ photos"}
          </Button>
          <Button
            variant="outline"
            onClick={backfillDetails}
            disabled={backfilling}
            data-testid="curated-backfill-details-btn"
            className="border-[#7A4EAB] text-[#7A4EAB] hover:bg-[#7A4EAB]/10"
            title="Fetch variants (sizes/lengths + prices) and full product descriptions from CJ for every imported product"
          >
            {backfilling ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Sparkles className="w-4 h-4 mr-1" />}
            {backfilling && detailProgress ? `Fetching ${detailProgress.processed}/${detailProgress.total}…` : "Pull variants & details"}
          </Button>
          <Button
            variant="outline"
            onClick={repriceAll}
            disabled={repricing}
            data-testid="curated-reprice-btn"
            className="border-[#C05A3C] text-[#C05A3C] hover:bg-[#C05A3C]/10"
            title="Recalculate every dropshipping product's price to 1.5× its supplier cost"
          >
            {repricing ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <PoundSterling className="w-4 h-4 mr-1" />}
            Re-price all to 1.5×
          </Button>
          <Button
            variant="outline"
            onClick={recategorizeAll}
            disabled={recategorizing}
            data-testid="curated-recategorize-btn"
            className="border-[#2A3D36] text-[#2A3D36] hover:bg-[#2A3D36]/10"
            title="Re-sort every product into the correct category (Men's Clothing, Beauty & Hair, Health & Wellness, Electronics, etc.) based on its title"
          >
            {recategorizing ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <FolderTree className="w-4 h-4 mr-1" />}
            Fix categories
          </Button>
          <Button
            onClick={() => setShowForm((v) => !v)}
            className="bg-[#1A2024] text-white hover:bg-[#1A2024]/90"
            data-testid="curated-add-btn"
          >
            <Plus className="w-4 h-4 mr-1" /> {showForm ? "Cancel" : "Add curated listing"}
          </Button>
        </div>
      </div>

      {/* Quick stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
        <Stat label="Listings" value={list.length} testid="curated-stat-listings" />
        <Stat label="Sales" value={totalSales} testid="curated-stat-sales" icon={<TrendingUp className="w-3 h-3" />} />
        <Stat label="Revenue" value={`£${totalRevenue.toFixed(2)}`} testid="curated-stat-revenue" icon={<PoundSterling className="w-3 h-3" />} />
        <Stat label="Est. profit" value={`£${totalEstMargin.toFixed(2)}`} testid="curated-stat-profit" accent="text-[#4A5D23]" />
      </div>

      {/* Add form */}
      {showForm && (
        <form onSubmit={submit} className="bg-[#F3EFE9] p-4 rounded-md mb-5 space-y-3" data-testid="curated-form">
          <div className="grid md:grid-cols-2 gap-3">
            <Field label="Title (public)" required>
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="curated-input-title" required />
            </Field>
            <Field label="Category" required>
              <Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} placeholder="Fashion / Beauty / Home decor / …" data-testid="curated-input-category" />
            </Field>
            <Field label="Selling price (£)" required>
              <Input type="number" step="0.01" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} data-testid="curated-input-price" required />
            </Field>
            <Field label="Country (display only)">
              <Input value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} data-testid="curated-input-country" />
            </Field>
          </div>
          <Field label="Description (public)" required>
            <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} data-testid="curated-input-desc" required />
          </Field>
          <Field label="Image URLs (one per line or comma-separated)">
            <Textarea value={form.images} onChange={(e) => setForm({ ...form, images: e.target.value })} rows={2} data-testid="curated-input-images" placeholder="https://… (paste Alibaba/AliExpress image URLs here)" />
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple
              onChange={uploadImages}
              className="hidden"
              data-testid="curated-image-file-input"
            />
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploadingImgs}
              data-testid="curated-upload-photos-btn"
              className="mt-2"
            >
              {uploadingImgs ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <ImagePlus className="w-4 h-4 mr-1" />}
              Upload photos from device
            </Button>
          </Field>

          <div className="border-t border-black/10 pt-3">
            <div className="label-overline text-[#C05A3C] mb-2">Private supplier info (admin-only)</div>
            <div className="grid md:grid-cols-2 gap-3">
              <Field label="Supplier URL (Alibaba/AliExpress)">
                <Input value={form.supplier_url} onChange={(e) => setForm({ ...form, supplier_url: e.target.value })} placeholder="https://www.aliexpress.com/item/…" data-testid="curated-input-supplier-url" />
              </Field>
              <Field label="Your cost (£)">
                <Input type="number" step="0.01" value={form.supplier_cost} onChange={(e) => setForm({ ...form, supplier_cost: e.target.value })} placeholder="e.g. 12.50" data-testid="curated-input-supplier-cost" />
              </Field>
            </div>
            <Field label="Supplier notes (admin-only)">
              <Textarea value={form.supplier_notes} onChange={(e) => setForm({ ...form, supplier_notes: e.target.value })} rows={2} placeholder="e.g. 'Best supplier: Yiwu Textile Co., 18-day shipping, requires bulk pack of 5'" data-testid="curated-input-supplier-notes" />
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Min shipping days">
                <Input type="number" value={form.estimated_shipping_min_days} onChange={(e) => setForm({ ...form, estimated_shipping_min_days: parseInt(e.target.value || "14", 10) })} data-testid="curated-input-ship-min" />
              </Field>
              <Field label="Max shipping days">
                <Input type="number" value={form.estimated_shipping_max_days} onChange={(e) => setForm({ ...form, estimated_shipping_max_days: parseInt(e.target.value || "28", 10) })} data-testid="curated-input-ship-max" />
              </Field>
            </div>
          </div>

          <Button type="submit" disabled={saving} className="bg-[#C05A3C] text-white hover:bg-[#C05A3C]/90" data-testid="curated-submit-btn">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
            Publish curated listing
          </Button>
        </form>
      )}

      {/* Landing showcase order — drag to reorder featured African pieces */}
      {featuredItems.length > 0 && (
        <div className="mb-6 rounded-md border border-[#D9923B]/30 bg-[#FBF6EE] p-4" data-testid="featured-reorder-panel">
          <div className="flex items-center gap-2 mb-1">
            <Sparkles className="w-4 h-4 text-[#D9923B]" />
            <span className="font-display text-lg">Landing showcase order</span>
            <Badge className="bg-[#D9923B] text-white text-[10px]">{featuredItems.length} featured</Badge>
          </div>
          <p className="text-xs text-muted-foreground mb-3">
            Drag to reorder — top items appear first in the “Featured African pieces” section. Note: pieces with real sales auto-bubble to the front as <span className="text-[#B84A28] font-medium">🔥 Bestsellers</span>; this manual order is the tiebreaker.
          </p>
          <div className="space-y-2" data-testid="featured-reorder-list">
            {featuredItems.map((l, idx) => (
              <div
                key={l.listing_id}
                draggable
                onDragStart={() => { dragItem.current = idx; }}
                onDragOver={(e) => e.preventDefault()}
                onDrop={() => onDrop(idx)}
                data-testid={`featured-reorder-row-${l.listing_id}`}
                className="flex items-center gap-3 bg-white border border-black/10 rounded-md p-2 cursor-grab active:cursor-grabbing hover:border-[#D9923B] transition-colors"
              >
                <GripVertical className="w-4 h-4 text-muted-foreground shrink-0" />
                <span className="text-xs font-mono text-[#D9923B] w-5 text-center">{idx + 1}</span>
                <img src={(l.images || [])[0] || "/img/placeholder.png"} alt={l.title} className="w-10 h-10 object-cover rounded" onError={(e) => { e.target.src = "/img/placeholder.png"; }} />
                <span className="text-sm font-medium truncate flex-1">{l.title}</span>
                <Button variant="ghost" size="sm" onClick={() => toggleFeature(l)} title="Remove from landing" data-testid={`featured-remove-${l.listing_id}`}>
                  <Star className="w-4 h-4 fill-[#D9923B] text-[#D9923B]" />
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* List of existing curated */}
      {loading ? (
        <div className="text-sm text-muted-foreground flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
      ) : list.length === 0 ? (
        <div className="text-sm text-muted-foreground italic" data-testid="curated-empty">
          No curated listings yet. Click &ldquo;Add curated listing&rdquo; above to seed the marketplace with Alibaba-sourced African-themed products.
        </div>
      ) : (
        <div className="space-y-3" data-testid="curated-list">
          {list.map((l) => (
            <div key={l.listing_id} className="flex items-start gap-3 p-3 border border-black/10 rounded-md bg-white" data-testid={`curated-row-${l.listing_id}`}>
              <img src={(l.images || [])[0] || "/img/placeholder.png"} alt={l.title} className="w-16 h-16 object-cover rounded" onError={(e) => { e.target.src = "/img/placeholder.png"; }} />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap mb-1">
                  <a href={`/listing/${l.listing_id}`} target="_blank" rel="noreferrer" className="font-semibold text-[#1A2024] hover:underline">{l.title}</a>
                  <Badge className="bg-[#C05A3C] text-white text-[10px]">£{(l.price || 0).toFixed(2)}</Badge>
                  {l.sales_count > 0 && <Badge className="bg-[#4A5D23] text-white text-[10px]">{l.sales_count} sold</Badge>}
                  {l.featured_landing && <Badge className="bg-[#D9923B] text-white text-[10px] inline-flex items-center gap-1"><Star className="w-2.5 h-2.5 fill-white" /> On landing</Badge>}
                </div>
                <div className="text-xs text-muted-foreground flex flex-wrap gap-x-3 gap-y-1 items-center">
                  <span>Category: <strong>{l.category}</strong></span>
                  {l.supplier_cost && <span>Cost: <strong>£{l.supplier_cost.toFixed(2)}</strong></span>}
                  {l.estimated_margin != null && <span className="text-[#4A5D23]">Margin: <strong>£{l.estimated_margin.toFixed(2)}{l.margin_pct != null ? ` (${l.margin_pct}%)` : ""}</strong></span>}
                  <span>Ships: <strong>{l.estimated_shipping_min_days}-{l.estimated_shipping_max_days}d</strong></span>
                  {l.is_overpriced && (
                    <Badge className="bg-[#B84A28] text-white text-[10px]" data-testid={`curated-overpriced-${l.listing_id}`}>Above competitor</Badge>
                  )}
                </div>
                {l.supplier_url && (
                  <a href={l.supplier_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-[#C05A3C] hover:underline mt-1">
                    <ExternalLink className="w-3 h-3" /> Open supplier
                  </a>
                )}
                {l.supplier_notes && <div className="text-xs text-muted-foreground italic mt-1 line-clamp-2">📝 {l.supplier_notes}</div>}
                <PriceEditor l={l} onSaved={load} />
              </div>
              <Button variant="ghost" size="sm" onClick={() => toggleFeature(l)} data-testid={`curated-feature-${l.listing_id}`} title={l.featured_landing ? "Showing on landing — click to remove" : "Show on landing page"}>
                <Star className={`w-4 h-4 ${l.featured_landing ? "fill-[#D9923B] text-[#D9923B]" : "text-muted-foreground"}`} />
              </Button>
              <Button variant="ghost" size="sm" onClick={() => remove(l.listing_id)} data-testid={`curated-delete-${l.listing_id}`}>
                <Trash2 className="w-4 h-4 text-red-500" />
              </Button>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
};

const PriceEditor = ({ l, onSaved }) => {
  const [open, setOpen] = useState(false);
  const [price, setPrice] = useState(l.price != null ? String(l.price) : "");
  const [competitor, setCompetitor] = useState(l.competitor_price != null ? String(l.competitor_price) : "");
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);

  const save = async () => {
    setSaving(true);
    try {
      const body = {};
      if (price !== "") body.price = parseFloat(price);
      body.competitor_price = competitor === "" ? null : parseFloat(competitor);
      const { data } = await api.patch(`/admin/curated-listings/${l.listing_id}/pricing`, body);
      setResult(data);
      toast.success("Pricing updated");
      onSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not update pricing");
    } finally {
      setSaving(false);
    }
  };

  const cost = l.supplier_cost;
  const suggested = l.suggested_price;

  return (
    <div className="mt-2">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid={`curated-smart-price-toggle-${l.listing_id}`}
        className="inline-flex items-center gap-1 text-xs font-semibold text-[#C05A3C] hover:underline"
      >
        <PoundSterling className="w-3 h-3" /> {open ? "Hide smart pricing" : "Smart pricing"}
      </button>
      {open && (
        <div className="mt-2 rounded-md border border-black/10 bg-[#FBF6EE] p-3 space-y-2" data-testid={`curated-smart-price-panel-${l.listing_id}`}>
          <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
            <span>Cost: <strong>{cost != null ? `£${cost.toFixed(2)}` : "—"}</strong></span>
            <span className="text-[#4A5D23]">Suggested (1.5×): <strong>{suggested != null ? `£${suggested.toFixed(2)}` : "—"}</strong></span>
            {l.margin_pct != null && <span>Current margin: <strong>{l.margin_pct}%</strong></span>}
          </div>
          <div className="flex flex-wrap items-end gap-2">
            <label className="block">
              <span className="text-[10px] uppercase tracking-wide text-muted-foreground block mb-0.5">Your price £</span>
              <Input type="number" step="0.01" value={price} onChange={(e) => setPrice(e.target.value)} data-testid={`curated-price-input-${l.listing_id}`} className="h-8 w-28" />
            </label>
            <label className="block">
              <span className="text-[10px] uppercase tracking-wide text-muted-foreground block mb-0.5">Competitor £ (e.g. Amazon)</span>
              <Input type="number" step="0.01" value={competitor} onChange={(e) => setCompetitor(e.target.value)} placeholder="optional" data-testid={`curated-competitor-input-${l.listing_id}`} className="h-8 w-32" />
            </label>
            {suggested != null && (
              <Button type="button" variant="outline" size="sm" onClick={() => setPrice(String(suggested))} data-testid={`curated-use-suggested-${l.listing_id}`} className="h-8 text-xs">
                Use suggested
              </Button>
            )}
            <Button type="button" size="sm" onClick={save} disabled={saving} data-testid={`curated-price-save-${l.listing_id}`} className="h-8 bg-[#C05A3C] text-white text-xs">
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Save"}
            </Button>
          </div>
          {competitor !== "" && price !== "" && parseFloat(price) > parseFloat(competitor) && (
            <div className="text-xs text-[#B84A28] font-medium" data-testid={`curated-price-warning-${l.listing_id}`}>
              ⚠️ Your price is above the competitor price — consider lowering to stay competitive.
            </div>
          )}
          {result && result.margin_pct != null && (
            <div className="text-xs text-[#4A5D23]" data-testid={`curated-price-result-${l.listing_id}`}>
              Saved · margin now £{result.estimated_margin?.toFixed(2)} ({result.margin_pct}%).
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const Field = ({ label, required, children }) => (
  <label className="block">
    <span className="text-xs font-medium text-[#1A2024] mb-1 block">{label}{required && <span className="text-[#C05A3C]"> *</span>}</span>
    {children}
  </label>
);

const Stat = ({ label, value, testid, icon, accent = "text-[#1A2024]" }) => (
  <div className="bg-[#F3EFE9] rounded-md p-3" data-testid={testid}>
    <div className="text-[11px] text-muted-foreground uppercase tracking-wide flex items-center gap-1">{icon}{label}</div>
    <div className={`font-display text-xl ${accent}`}>{value}</div>
  </div>
);

export default CuratedListings;
